import { c as _c } from "react/compiler-runtime";
import type { MPARequest } from '@repo/api-gw-sdk';
import { type Configuration, LakehouseApi, type Middleware, type RequestContext, type ResponseContext } from '@repo/lakehouse-sdk';
import { useMemo } from 'react';
import useSWR, { type SWRConfiguration } from 'swr';
import { useNonProjectDal } from '@/contexts/useNonProjectDal';
import { useUser } from '@/contexts/useUser';
import { updatePreviewOutcome } from '@/data/dal/lakehouseUpdatePreview';
import { getIngestionPipelineTarget } from './mpaStreams';
import { listProjectIngestionPipelines } from './projectStreams';

// Approval screens live in the host, outside the Data Platform console, so they
// build the one lakehouse client they need instead of mounting its DAL.
const useLakehouseApi = () => {
  const $ = _c(2);
  const {
    lakehouseConfiguration
  } = useNonProjectDal();
  let t0;
  if ($[0] !== lakehouseConfiguration) {
    t0 = new LakehouseApi(lakehouseConfiguration);
    $[0] = lakehouseConfiguration;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
const httpStatusOf = (error: unknown) => typeof error === 'object' && error !== null && 'code' in error ? error.code : undefined;

// A stream deleted since, or one the viewer may not read, is shown by its ID:
// neither is a failure worth reporting or retrying.
export const isUnavailable = (error: unknown) => {
  const status = httpStatusOf(error);
  return status === 403 || status === 404;
};

// Streams change in the Data Platform console, which never invalidates these
// keys, so the reads keep SWR's revalidation on mount and on focus.
const STREAM_SWR_OPTIONS: SWRConfiguration = {
  onError: (error: unknown) => {
    if (!isUnavailable(error)) {
      window.reportError(error);
    }
  },
  shouldRetryOnError: (error: unknown) => !isUnavailable(error)
};
export const useProjectStreams = () => {
  const $ = _c(5);
  const api = useLakehouseApi();
  const {
    currentProjectId
  } = useUser();
  let t0;
  if ($[0] !== currentProjectId) {
    t0 = currentProjectId ? ["mpaProjectStreams", currentProjectId] : null;
    $[0] = currentProjectId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== api || $[3] !== currentProjectId) {
    t1 = () => listProjectIngestionPipelines(api, currentProjectId);
    $[2] = api;
    $[3] = currentProjectId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useSWR(t0, t1, STREAM_SWR_OPTIONS);
};
export const useStream = (workspaceId, pipelineId) => {
  const $ = _c(9);
  const api = useLakehouseApi();
  const {
    currentProjectId
  } = useUser();
  let t0;
  if ($[0] !== currentProjectId || $[1] !== pipelineId || $[2] !== workspaceId) {
    t0 = currentProjectId ? ["mpaStream", currentProjectId, workspaceId, pipelineId] : null;
    $[0] = currentProjectId;
    $[1] = pipelineId;
    $[2] = workspaceId;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] !== api || $[5] !== currentProjectId || $[6] !== pipelineId || $[7] !== workspaceId) {
    t1 = () => api.getIngestionPipeline(currentProjectId, workspaceId, pipelineId);
    $[4] = api;
    $[5] = currentProjectId;
    $[6] = pipelineId;
    $[7] = workspaceId;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return useSWR(t0, t1, STREAM_SWR_OPTIONS);
};
const sendStoredBody = (body: string): Middleware => ({
  pre: (context: RequestContext) => {
    context.setBody(body);
    return Promise.resolve(context);
  },
  post: (context: ResponseContext) => Promise.resolve(context)
});

/**
 * Previews a stored partial update. The SDK re-serializes a typed body through
 * its model, dropping every field this console's spec does not know, so the
 * stored body is sent as is: the preview checks the call apply replays.
 */
export const previewStoredStreamUpdate = (configuration: Configuration, target: {
  projectId: string;
  workspaceId: string;
  pipelineId: string;
}, requestBody: string) => updatePreviewOutcome(() => new LakehouseApi({
  ...configuration,
  middleware: [sendStoredBody(requestBody), ...(configuration.middleware ?? [])]
}).previewIngestionPipelineUpdate(target.projectId, target.workspaceId, target.pipelineId, {}));

// The project comes from the stored path, not the viewer's current project.
export const useStoredStreamUpdatePreview = request => {
  const $ = _c(8);
  const {
    lakehouseConfiguration
  } = useNonProjectDal();
  let t0;
  if ($[0] !== request) {
    t0 = getIngestionPipelineTarget(request);
    $[0] = request;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const target = t0;
  const requestBody = request.requestBody ?? "";
  let t1;
  if ($[2] !== requestBody || $[3] !== target) {
    t1 = target && requestBody ? ["previewStoredIngestionPipelineUpdate", target.projectId, target.workspaceId, target.pipelineId, requestBody] as const : null;
    $[2] = requestBody;
    $[3] = target;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== lakehouseConfiguration) {
    t2 = t3 => {
      const [, projectId, workspaceId, pipelineId, body] = t3;
      return previewStoredStreamUpdate(lakehouseConfiguration, {
        projectId,
        workspaceId,
        pipelineId
      }, body);
    };
    $[5] = lakehouseConfiguration;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      revalidateOnFocus: false
    };
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useSWR(t1, t2, t3);
};